import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MarketField, MarketInputText, MarketButton } from '@market/react';
import { RedeemRedemptionCodeResponse } from 'rpc/model/squareup/rewards/service';

import { useRedeemRedemptionCode } from 'typescript/hooks/redeem/use_redeem_redemption_code';
import { parseResponseError } from '../utils';
import styles from './redeem_form.module.scss';

export const REDEEM_INPUT_TEST_ID = 'redeem-input-text';
export const INPUT_NAME = 'code';

interface RedeemFormValues {
  code: string;
}

interface RedeemFormProps {
  onSuccess: () => void;
}

const RedeemForm = ({ onSuccess }: RedeemFormProps) => {
  const { t } = useTranslation();
  const [apiError, setApiError] = useState<string | null>(null);
  const { redeemCode, isPending } = useRedeemRedemptionCode();

  const { control, handleSubmit } = useForm<RedeemFormValues>({
    defaultValues: {
      code: '',
    },
  });

  const onSubmit = (data: RedeemFormValues) => {
    setApiError(null);

    redeemCode(data.code, {
      onSuccess: (status) => {
        if (status === RedeemRedemptionCodeResponse.Status.SUCCESS) {
          onSuccess();
        } else {
          setApiError(parseResponseError(status));
        }
      },
      onError: () => {
        setApiError('error.api.generic');
      },
    });
  };

  return (
    <form className={styles.redeemForm} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name={INPUT_NAME}
        rules={{
          required: true,
          validate: (value) => Boolean(value.trim()),
        }}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error: formError },
        }) => (
          <MarketField
            className={styles.redeemTextInput}
            invalid={Boolean(formError) || Boolean(apiError)}
            disabled={isPending}
          >
            <MarketInputText
              data-testid={REDEEM_INPUT_TEST_ID}
              size="large"
              value={value}
              onBlur={onBlur}
              onMarketInputValueChange={(e) => {
                if (apiError) {
                  setApiError(null);
                }

                onChange(e.detail.value);
              }}
            >
              <label>{t('redeem.form.textInputLabel')}</label>
            </MarketInputText>
            {formError && (
              <small slot="error">{t('redeem.error.required')}</small>
            )}
            {apiError && !formError && (
              <small slot="error">{t(apiError)}</small>
            )}
          </MarketField>
        )}
      />
      <MarketButton
        className={styles.redeemCta}
        type="submit"
        rank="primary"
        size="medium"
        disabled={isPending}
      >
        {t('redeem.form.cta')}
      </MarketButton>
    </form>
  );
};

export default RedeemForm;
